import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MyPromptForm } from "./MyPromptForm";

export const MyPromptsDialog = ({ prompts, onChange, onClose }) => {
  const mine = prompts.filter((p) => p.is_private);
  const [editing, setEditing] = useState(mine.length === 0 ? {} : null);
  const [deleting, setDeleting] = useState(null);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    setDeleting(p.id);
    try {
      await api.delete(`/inquiry-prompts/mine/${p.id}`);
      onChange(prompts.filter((x) => x.id !== p.id));
      toast.success(`Deleted "${p.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const saved = (doc) => {
    const exists = prompts.some((p) => p.id === doc.id);
    onChange(exists ? prompts.map((p) => (p.id === doc.id ? doc : p)) : [...prompts, doc]);
    setEditing(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="my-prompts-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#007AFF]" /> My drafting methods
          </DialogTitle>
          <DialogDescription className="text-xs">
            Private to your account. They appear in your "Drafting method" dropdown alongside the shared library.
          </DialogDescription>
        </DialogHeader>

        {editing !== null ? (
          <MyPromptForm prompt={editing} onSaved={saved} onCancel={() => setEditing(null)} />
        ) : (
          <div className="space-y-3">
            {mine.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="my-prompts-empty">No personal methods yet.</p>
            ) : (
              <div className="space-y-2" data-testid="my-prompts-list">
                {mine.map((p) => (
                  <div key={p.id} className="border border-border bg-background p-3 flex items-start gap-3" data-testid={`my-prompt-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-bold tracking-tight text-sm">{p.name}</p>
                      {p.description && <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>}
                      <p className="text-[10px] text-muted-foreground font-mono mt-1.5 line-clamp-2">{p.prompt}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => setEditing(p)} title="Edit" data-testid={`my-prompt-edit-${p.id}`}
                        className="w-7 h-7 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground hover:border-[#007AFF]/50 rounded-sm transition-colors duration-200">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(p)} disabled={deleting === p.id} title="Delete" data-testid={`my-prompt-delete-${p.id}`}
                        className="w-7 h-7 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200">
                        {deleting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing({})} data-testid="my-prompt-new-btn"
              className="rounded-sm gap-1.5 h-8 text-xs border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF]">
              <Plus className="w-3.5 h-3.5" /> New method
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
